import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw } from "lucide-react";

export default function History() {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(1);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const fetchHistory = useCallback(() => {
    setLoading(true);
    setError("");
    api("get", `/admin/transactions?days=${days}&size=50`)
      .then((res) => {
        if (res.data.status) {
          setTransactions(res.data.data || []);
        } else {
          setError(res.data.message || "Tidak ada data");
          setTransactions([]);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.detail || "Gagal mengambil data. Pastikan credentials GoBiz sudah diisi di Settings.");
        setTransactions([]);
      })
      .finally(() => setLoading(false));
  }, [api, days]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    setPage(1);
  }, [days]);

  const totalPages = Math.max(1, Math.ceil(transactions.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleTransactions = transactions.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div data-testid="history-page" className="space-y-6 sm:space-y-8">
      <div className="flex flex-col gap-4 rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">History</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">Transaction History</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Riwayat transaksi GoBiz dengan tampilan yang lebih aman dibaca di layar kecil.</p>
        </div>
        <button data-testid="refresh-history" onClick={fetchHistory} className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-border bg-background/80 hover:bg-secondary transition-colors">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {[1, 3, 7, 30].map((d) => (
          <button
            key={d}
            data-testid={`days-${d}`}
            onClick={() => setDays(d)}
            className={`rounded-2xl border px-4 py-2 text-sm font-medium transition-colors ${
              days === d ? "bg-primary text-primary-foreground border-primary" : "border-border bg-background/80 text-muted-foreground hover:bg-secondary"
            }`}
          >
            {d} hari
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="history-error" className="border border-amber-300 bg-amber-50 text-amber-800 text-sm px-4 py-3 rounded-sm mb-4">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-border/70 bg-card/90 shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/50">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Tx ID</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Amount</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">Time</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Memuat transaksi...</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Tidak ada transaksi</td></tr>
            ) : (
              visibleTransactions.map((tx, i) => (
                <tr key={tx.tx_id || i} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                  <td className="px-4 py-3"><code className="font-mono text-xs">{(tx.tx_id || "-").toString().slice(0, 16)}...</code></td>
                  <td className="px-4 py-3 text-right font-mono font-medium">{tx.amount_display || `Rp ${(tx.amount || 0).toLocaleString("id-ID")}`}</td>
                  <td className="px-4 py-3 text-muted-foreground uppercase text-xs font-medium">{tx.payment_type || "-"}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold uppercase text-green-700">{tx.status || "-"}</span></td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{tx.time ? new Date(tx.time).toLocaleString("id-ID") : "-"}</td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>

      {!loading && transactions.length > 0 && (
        <div className="flex flex-col gap-3 rounded-3xl border border-border/70 bg-card/90 px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Menampilkan {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, transactions.length)} dari {transactions.length} transaksi
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Prev
            </button>
            <span className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
